import { useRPCContext } from "../../contexts/RPCContext.js";
import { useRPC } from "../../hooks/useRPC/index.js";
import { useStateDebounced } from "../../hooks/useStateDebounced.js";
import { useEffect, useMemo, useReducer, useState } from "react";
import { Select } from "@therms/atalaya";
import { jsx } from "react/jsx-runtime";
import { castArray } from "lodash-es";
// src/components/Select/SelectAsyncRPC.tsx
var reducer = (state, { key, options, type }) => {
	if (type === "additional") {
		const _options = [...state.options, ...options];
		return {
			key,
			options: _options,
			total: _options.length
		};
	} else if (type === "reset") return {
		key,
		options,
		total: options.length
	};
	return state;
};
var SelectAsyncRPC = ({ additionalOptions, args, idProp = "id", limit = 15, multi, request, requestFilterProp = "idFilter", responseOptionsExtractor, responseValuesExtractor, selectedArgs, value, ...atalayaSelectProps }) => {
	const { rpcClient } = useRPCContext();
	const [skip, setSkip] = useState(0);
	const [, debouncedSearchText, setSearchText] = useStateDebounced("");
	const [response, dispatch] = useReducer(reducer, {
		key: "",
		options: additionalOptions || [],
		total: additionalOptions?.length || 0
	});
	const [selectedOptions, setSelectedOptions] = useState([]);
	const _limit = useMemo(() => {
		if (!multi) return limit;
		return limit + castArray(value).length;
	}, [
		limit,
		multi,
		value
	]);
	const query = useMemo(() => {
		return {
			...args,
			pagination: {
				limit: _limit,
				skip
			},
			searchText: debouncedSearchText
		};
	}, [
		args,
		debouncedSearchText,
		_limit,
		value,
		skip
	]);
	const { call, data, isCalling: loading } = useRPC(request, query);
	const handleLoadOptions = ({ additional, text } = {}) => {
		if (text !== debouncedSearchText) {
			setSearchText(text || "");
			setSkip(0);
			return;
		}
		if (loading) return;
		if (additional && data.total > response?.total) setSkip(skip + _limit);
	};
	useEffect(() => {
		if (skip !== 0) setSkip(0);
	}, [args]);
	useEffect(() => {
		if (!data || response?.key === JSON.stringify(query)) return;
		const _options = responseOptionsExtractor(data);
		if (Array.isArray(_options)) dispatch({
			key: JSON.stringify(query),
			options: responseOptionsExtractor(data),
			type: skip ? "additional" : "reset"
		});
	}, [data]);
	const handleCreateOption = (option) => {
		const onCreateReturnValue = atalayaSelectProps.onCreateOption?.(option);
		if (onCreateReturnValue?.finally) onCreateReturnValue.finally(() => {
			call();
		});
		else call();
	};
	useEffect(() => {
		if (!value || !response?.options.length) return setSelectedOptions([]);
		const _value = castArray(value);
		const fetchedOptions = [];
		const idsToFetch = [];
		const _responseValuesExtractor = responseValuesExtractor || responseOptionsExtractor;
		_value.forEach((selectedOptionOrId) => {
			let selectedId = selectedOptionOrId;
			if (typeof selectedOptionOrId === "object") selectedId = selectedOptionOrId[idProp];
			let allOptions = [...response.options];
			if (additionalOptions) allOptions = [...allOptions, ...additionalOptions];
			if (typeof selectedOptionOrId === "object") allOptions = [...allOptions, ..._value];
			const fetchedOption = allOptions.find((option) => option[idProp] === selectedId);
			if (fetchedOption) fetchedOptions.push(fetchedOption);
			else idsToFetch.push(selectedId);
		});
		if (idsToFetch.length) rpcClient.call(request, {
			...selectedArgs,
			[requestFilterProp]: idsToFetch
		}).then((_response) => {
			const responseOptions = _responseValuesExtractor(_response.data);
			setSelectedOptions([...fetchedOptions, ...responseOptions]);
		});
		else setSelectedOptions(fetchedOptions);
	}, [
		args,
		idProp,
		requestFilterProp,
		value,
		response?.options,
		responseOptionsExtractor,
		responseValuesExtractor,
		selectedArgs
	]);
	return /* @__PURE__ */ jsx(Select, {
		filterOption: null,
		isLoading: loading,
		loadOptions: handleLoadOptions,
		multi,
		options: response.options,
		value: selectedOptions,
		onCreateOption: handleCreateOption,
		...atalayaSelectProps
	});
};
export { SelectAsyncRPC };

//# sourceMappingURL=SelectAsyncRPC.js.map